import clsx from "clsx";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

type Tone = "brand" | "green" | "red" | "amber" | "slate";

const toneStyles: Record<Tone, string> = {
  brand: "text-brand-700 bg-brand-50",
  green: "text-emerald-700 bg-emerald-50",
  red: "text-red-700 bg-red-50",
  amber: "text-amber-700 bg-amber-50",
  slate: "text-slate-700 bg-slate-50",
};

export function KpiCard({
  label,
  value,
  hint,
  delta,
  icon: Icon,
  tone = "slate",
}: {
  label: ReactNode;
  value: ReactNode;
  hint?: ReactNode;
  delta?: { value: number; label?: string };
  icon?: LucideIcon;
  tone?: Tone;
}) {
  return (
    <div className="card p-4 hover:shadow-card transition-shadow">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">
            {label}
          </div>
          <div className="mt-1.5 text-xl font-semibold text-slate-900 font-mono">
            {value}
          </div>
          {(hint || delta) && (
            <div className="mt-1 text-xs text-slate-500 flex items-center gap-2">
              {delta !== undefined && (
                <span
                  className={clsx(
                    "inline-flex items-center gap-0.5 font-medium",
                    delta.value > 0
                      ? "text-emerald-600"
                      : delta.value < 0
                      ? "text-red-600"
                      : "text-slate-500"
                  )}
                >
                  {delta.value > 0 ? "▲" : delta.value < 0 ? "▼" : "•"}{" "}
                  {Math.abs(delta.value)}
                  {delta.label ? ` ${delta.label}` : "%"}
                </span>
              )}
              {hint && <span>{hint}</span>}
            </div>
          )}
        </div>
        {Icon && (
          <div className={clsx("p-2 rounded-lg", toneStyles[tone])}>
            <Icon size={18} />
          </div>
        )}
      </div>
    </div>
  );
}
